// Meta (Facebook) ad attribution capture.
//
// The marketing site (lightmaster.io) initializes the Meta Pixel, which drops
// the `_fbp` browser id cookie and — on an ad click (?fbclid=) — the `_fbc`
// click id. Some of those are scoped to `.lightmaster.io` (mirrored as `lm_fbp`)
// so they're readable cross-subdomain on app.lightmaster.io. We also accept
// `?fbclid=` directly on the app (direct ad links / local testing) and build the
// `fbc` value ourselves per Meta's spec: `fb.1.<unix_ms>.<fbclid>`. UTM params on
// the URL are captured too. Whatever we find is persisted to localStorage so it
// survives the multi-step signup, then included in the signUp metadata and
// consumed server-side (org columns, Stripe checkout metadata, Meta CAPI, and the
// set_contact_attribution() RPC for CRM segmentation).
//
// To mock the marketing-site handoff for testing, set the cookie or use ?fbclid=:
//   document.cookie = 'lm_fbp=fb.1.1690000000000.1234567890; path=/';
//   or visit /auth?fbclid=IwAR_test123&utm_source=facebook&utm_campaign=spring

const STORAGE_KEYS = {
  fbc: 'meta_fbc',
  fbp: 'meta_fbp',
  fbclid: 'fbclid',
  utm_source: 'utm_source',
  utm_medium: 'utm_medium',
  utm_campaign: 'utm_campaign',
  utm_term: 'utm_term',
  utm_content: 'utm_content',
} as const;

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

export type MetaAttribution = {
  meta_fbc: string | null;
  meta_fbp: string | null;
  fbclid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
};

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Capture Meta click/browser ids and UTM params from the URL/cookies and persist
 * them. Call once on app load.
 */
export function captureMetaAttributionFromEnv(): void {
  try {
    const params = new URLSearchParams(window.location.search);

    // fbclid → fbc (Meta spec: fb.1.<unix_ms>.<fbclid>). Prefer a real _fbc /
    // lm_fbc cookie if the Pixel already minted one; otherwise build it.
    const fbclid = (params.get('fbclid') || '').trim();
    if (fbclid) {
      localStorage.setItem(STORAGE_KEYS.fbclid, fbclid);
    }

    const fbcCookie = readCookie('_fbc') || readCookie('lm_fbc');
    const fbc =
      fbcCookie ||
      (fbclid ? `fb.1.${Date.now()}.${fbclid}` : null) ||
      readStored(STORAGE_KEYS.fbc);
    if (fbc) localStorage.setItem(STORAGE_KEYS.fbc, fbc);

    // fbp browser id from the Pixel's cookie.
    const fbp = readCookie('_fbp') || readCookie('lm_fbp') || readStored(STORAGE_KEYS.fbp);
    if (fbp) localStorage.setItem(STORAGE_KEYS.fbp, fbp);

    // UTM params.
    for (const key of UTM_KEYS) {
      const value = (params.get(key) || '').trim();
      if (value) localStorage.setItem(STORAGE_KEYS[key], value);
    }
  } catch {
    /* ignore */
  }
}

/** The captured Meta attribution, if any (localStorage, falling back to cookies). */
export function getMetaAttribution(): MetaAttribution {
  return {
    meta_fbc: readStored(STORAGE_KEYS.fbc) || readCookie('_fbc') || readCookie('lm_fbc'),
    meta_fbp: readStored(STORAGE_KEYS.fbp) || readCookie('_fbp') || readCookie('lm_fbp'),
    fbclid: readStored(STORAGE_KEYS.fbclid),
    utm_source: readStored(STORAGE_KEYS.utm_source),
    utm_medium: readStored(STORAGE_KEYS.utm_medium),
    utm_campaign: readStored(STORAGE_KEYS.utm_campaign),
    utm_term: readStored(STORAGE_KEYS.utm_term),
    utm_content: readStored(STORAGE_KEYS.utm_content),
  };
}

/** Clear after a successful signup so it isn't reused. */
export function clearMetaAttribution(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.fbc);
    localStorage.removeItem(STORAGE_KEYS.fbp);
    localStorage.removeItem(STORAGE_KEYS.fbclid);
    for (const key of UTM_KEYS) localStorage.removeItem(STORAGE_KEYS[key]);
  } catch {
    /* ignore */
  }
}
